import { useState, useEffect, useMemo, useCallback } from "react";
import {
  Modal,
  BlockStack,
  InlineStack,
  Text,
  Box,
  SkeletonBodyText,
  Filters,
  ChoiceList,
  ResourceList,
  ResourceItem,
  Bleed,
} from "@medentic/polaris-react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import useApiRequest from "@shared/hooks/common/useApiRequest";
import { useCountries } from "@shared/hooks/common/useCountries";
import { SearchableChoiceList } from "@shared/ui";
import EventsDefaultAvatar from "@features/events/components/EventsDefaultAvatar";
import { getLocalizedText } from "@shared/lib";

// Import Venue type from EventVenues
import type { Venue } from "@features/events/types";

interface AssignLocationsModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedLocationIds: string[];
  onAssign: (locationIds: string[]) => void;
}

export default function AssignLocationsModal({
  isOpen,
  onClose,
  selectedLocationIds,
  onAssign,
}: AssignLocationsModalProps) {
  const { t } = useTranslation();
  const { eventOrganizerId: hcc } = useParams<{ eventOrganizerId: string }>();
  const { GET } = useApiRequest();
  const { countries } = useCountries();

  // Local state for modal
  const [localSelectedIds, setLocalSelectedIds] = useState<string[] | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCountry, setSelectedCountry] = useState("");
  const [previouslyUsedFilter, setPreviouslyUsedFilter] =
    useState<string>("used");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [hasUserSetFilter, setHasUserSetFilter] = useState(false);
  const selectedIds = localSelectedIds ?? selectedLocationIds;

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch all venues (no server-side filtering)
  const { data: allVenues = [], isLoading } = useQuery<Venue[]>({
    queryKey: ["assign-locations", hcc],
    queryFn: () => GET(`/api/healthcare-companies/${hcc}/e/venues`),
    enabled: Boolean(hcc && isOpen),
    placeholderData: keepPreviousData,
  });

  const hasUsedVenues = useMemo(
    () => allVenues.some((venue: Venue) => venue.isUsedBefore === true),
    [allVenues]
  );

  const effectivePreviouslyUsedFilter =
    !hasUserSetFilter && previouslyUsedFilter === "used" && !hasUsedVenues
      ? ""
      : previouslyUsedFilter;

  // Filter venues locally
  const venues = useMemo(() => {
    let filtered = [...allVenues];

    // Apply search filter
    if (debouncedQuery) {
      const searchLower = debouncedQuery.toLowerCase();
      filtered = filtered.filter(
        (venue: Venue) =>
          getLocalizedText(venue.name)?.toLowerCase().includes(searchLower) || 
          getLocalizedText(venue.geoAddress?.summary)?.toLowerCase().includes(searchLower)
      );
    }

    // Apply country filter
    if (selectedCountry) {
      filtered = filtered.filter(
        (venue: Venue) =>
          venue.geoAddress?.countryISO3?.toLowerCase() === selectedCountry
      );
    }

    // Apply previously used filter
    if (effectivePreviouslyUsedFilter === "used") {
      filtered = filtered.filter((venue: Venue) => venue.isUsedBefore === true);
    } else if (effectivePreviouslyUsedFilter === "notUsed") {
      filtered = filtered.filter(
        (venue: Venue) => venue.isUsedBefore === false
      );
    }

    return filtered;
  }, [allVenues, debouncedQuery, selectedCountry, effectivePreviouslyUsedFilter]);

  // Handle venue selection
  const handleVenueToggle = (venueId: string) => {
    setLocalSelectedIds((prev) => {
      const current = prev ?? selectedLocationIds;
      return current.includes(venueId)
        ? current.filter((id) => id !== venueId)
        : [...current, venueId];
    });
  };

  const resetModalState = useCallback(() => {
    setLocalSelectedIds(null);
    setSearchQuery("");
    setSelectedCountry("");
    setPreviouslyUsedFilter("used");
    setHasUserSetFilter(false);
  }, []);

  // Handle assign
  const handleAssign = () => {
    onAssign(selectedIds);
    resetModalState();
    onClose();
  };

  // Handle cancel
  const handleCancel = () => {
    resetModalState();
    onClose();
  };

  // Clear filters
  const handleClearFilters = () => {
    setSearchQuery("");
    setSelectedCountry("");
    setPreviouslyUsedFilter("");
    setHasUserSetFilter(true);
  };

  // Country options from API
  const countryOptions = [
    { label: t("common.allCountries"), value: "" },
    ...countries.map((country) => ({
      label: country.name,
      value: country.iso3?.toLowerCase(),
    })),
  ];

  // Filters configuration
  const filters = [
    {
      key: "country",
      label: t("common.country"),
      filter: (
        <SearchableChoiceList
          label={t("common.country")}
          options={countryOptions}
          value={selectedCountry}
          onChange={(value) => setSelectedCountry(value)}
        />
      ),
      shortcut: true,
    },
    {
      key: "previouslyUsed",
      label: t("common.previouslyUsed"),
      filter: (
        <ChoiceList
          title={t("common.previouslyUsed")}
          titleHidden
          choices={[
            {
              label: t("common.previouslyUsed"),
              value: "used",
            },
            {
              label: t("common.notPreviouslyUsed"),
              value: "notUsed",
            },
          ]}
          selected={
            effectivePreviouslyUsedFilter ? [effectivePreviouslyUsedFilter] : []
          }
          onChange={(value) => {
            setPreviouslyUsedFilter(value[0] || "");
            setHasUserSetFilter(true); // Mark as manually set by user
          }}
          allowMultiple={false}
        />
      ),
      shortcut: true,
    },
  ];

  // Applied filters
  const appliedFilters = [];
  if (selectedCountry) {
    const countryLabel =
      countryOptions.find((c) => c.value === selectedCountry)?.label ||
      selectedCountry;
    appliedFilters.push({
      key: "country",
      label: `${t("common.country")}: ${countryLabel}`,
      onRemove: () => setSelectedCountry(""),
    });
  }
  if (effectivePreviouslyUsedFilter) {
    const filterLabel =
      effectivePreviouslyUsedFilter === "used"
        ? t("common.previouslyUsed")
        : t("common.notPreviouslyUsed");
    appliedFilters.push({
      key: "previouslyUsed",
      label: filterLabel,
      onRemove: () => {
        setPreviouslyUsedFilter("");
        setHasUserSetFilter(true); // Mark as manually set by user
      },
    });
  }

  return (
    <Modal
      open={isOpen}
      onClose={handleCancel}
      title={t("eventDetails.locations.modal.title")}
      size="large"
      primaryAction={{
        content: t("eventDetails.locations.modal.assignButton"),
        onAction: handleAssign,
      }}
      secondaryActions={[
        {
          content: t("common.cancel"),
          onAction: handleCancel,
        },
      ]}
      onTransitionEnd={() => {
        document.body.setAttribute("data-lock-scrolling-hidden", "true");
      }}
    >
      <Modal.Section>
        <BlockStack gap="400">
          {/* Hint */}
          <Text as="p" variant="bodyMd" tone="subdued">
            {t("eventDetails.locations.modal.hint")}
          </Text>
        </BlockStack>

        <Bleed marginInline="400" marginBlock="400">
          {/* Venues list with integrated filtering */}
          {isLoading ? (
            <Box padding="400">
              <BlockStack gap="200">
                {[1, 2, 3].map((i) => (
                  <InlineStack key={i} gap="200" blockAlign="center">
                    <Box width="24px" minHeight="24px" />
                    <Box minHeight="40px">
                      <SkeletonBodyText lines={1} />
                    </Box>
                    <BlockStack gap="100">
                      <Box
                        width="200px"
                        background="bg-surface"
                        minHeight="16px"
                      />
                      <Box
                        width="150px"
                        background="bg-surface"
                        minHeight="12px"
                      />
                    </BlockStack>
                  </InlineStack>
                ))}
              </BlockStack>
            </Box>
          ) : (
            <ResourceList
              resourceName={{ singular: "location", plural: "locations" }}
              items={venues}
              selectedItems={selectedIds}
              onSelectionChange={(selection) =>
                setLocalSelectedIds(selection as string[])
              }
              selectable
              filterControl={
                <Filters
                  queryValue={searchQuery}
                  queryPlaceholder={t(
                    "eventDetails.locations.modal.searchPlaceholder"
                  )}
                  onQueryChange={setSearchQuery}
                  onQueryClear={() => setSearchQuery("")}
                  onClearAll={handleClearFilters}
                  filters={filters}
                  appliedFilters={appliedFilters}
                  closeOnChildOverlayClick
                />
              }
              emptyState={
                <Box padding="400">
                  <Text
                    as="p"
                    variant="bodyMd"
                    alignment="center"
                    tone="subdued"
                  >
                    {t("eventDetails.locations.modal.noLocations")}
                  </Text>
                </Box>
              }
              renderItem={(venue: Venue) => {
                const venueName = getLocalizedText(venue.name);

                return (
                  <ResourceItem
                    id={venue.id}
                    media={
                      <EventsDefaultAvatar
                        name={venue.name}
                        singleImage={venue.videoImage}
                        media={venue.media}
                      />
                    }
                    accessibilityLabel={`Select ${venueName}`}
                    onClick={() => handleVenueToggle(venue.id)}
                    verticalAlignment="center"
                  >
                    <InlineStack
                      align="space-between"
                      blockAlign="center"
                      wrap={false}
                    >
                      <BlockStack gap="100">
                        <Text
                          as="h3"
                          variant="bodyMd"
                          fontWeight="semibold"
                          breakWord
                        >
                          {venueName}
                        </Text>
                        <Text as="p" variant="bodySm" tone="subdued">
                          {getLocalizedText(venue.geoAddress?.summary)}
                        </Text>
                      </BlockStack>
                      <InlineStack align="end" gap="200">
                        {venue.isUsedBefore && (
                          <Text as="span" variant="bodySm" tone="subdued">
                            {t("common.previouslyUsed")}
                          </Text>
                        )}
                      </InlineStack>
                    </InlineStack>
                  </ResourceItem>
                );
              }}
            />
          )}
        </Bleed>
      </Modal.Section>
    </Modal>
  );
}
